import { useState, useRef, useEffect } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { File } from '@awesome-cordova-plugins/file';
import { vibrate, HapticPatterns } from '../../utils/haptics';

interface AudioRecorderProps {
  onAudioSave: (audioUrl: string) => void;
  isIdeasPage?: boolean;
}

const nativeFileName = Capacitor.getPlatform() === 'ios' ? 'recording.m4a' : 'recording.mp3';

export default function AudioRecorder({ onAudioSave, isIdeasPage = false }: AudioRecorderProps) {
  const { user } = useAuth();
  const { isPremium } = useUser();
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const mediaRecorderRef = useRef<any>(null);
  const filePathRef = useRef<string | null>(null);

  const startRecording = async () => {
    vibrate(HapticPatterns.MEDIUM);
    if (!user) {
      setError('You must be logged in to record audio');
      return;
    }

    setError(null);

    if (Capacitor.getPlatform() === 'web') {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const recorder = new MediaRecorder(stream);
        const chunks: Blob[] = [];

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunks.push(e.data);
        };

        recorder.onstop = async () => {
          const blob = new Blob(chunks, { type: 'audio/webm' });

          const filename = `${isIdeasPage ? 'ideabank' : 'recordings'}/${user.uid}/${Date.now()}.webm`;
          const storageRef = ref(storage, filename);

          setIsUploading(true);
          await uploadBytes(storageRef, blob);
          const downloadUrl = await getDownloadURL(storageRef);
          onAudioSave(downloadUrl);
          setIsUploading(false);
        };

        mediaRecorderRef.current = recorder;
        recorder.start();
        setIsRecording(true);
        setRecordingTime(0);
        timerRef.current = setInterval(() => setRecordingTime((prev) => prev + 1), 1000);
      } catch (err) {
        console.error('Web recording error:', err);
        setError('Failed to start recording');
      }
    } else {
      try {
        console.log('Starting native recording...');
        const MediaPlugin = (await import('@awesome-cordova-plugins/media')).Media;
        console.log('Media plugin module loaded');
        const File = (await import('@awesome-cordova-plugins/file')).File;
        console.log('File plugin module loaded');

        // Get proper platform paths
        const filePath = Capacitor.getPlatform() === 'ios'
          ? File.tempDirectory
          : File.externalDataDirectory;
        console.log('Resolved native file path:', filePath);

        const fileName = Capacitor.getPlatform() === 'ios' ? 'recording.m4a' : 'recording.mp3';
        const fullPath = filePath + fileName;
        console.log('Will record to path:', fullPath);
        
        // Clean up existing media if any
        if (mediaRecorderRef.current) {
          try {
            mediaRecorderRef.current.release();
          } catch (e) {
            console.log('No media to release');
          }
          mediaRecorderRef.current = null;
        }

        // Clean up any existing file
        try {
          await File.removeFile(filePath, fileName);
          console.log('Cleaned up existing recording file');
        } catch (e) {
          console.log('No existing file to clean up');
        }
        
        // Small delay after cleanup
        await new Promise(resolve => setTimeout(resolve, 100));
        
        console.log('Creating Media instance...');
        const file = MediaPlugin.create(fullPath);
        filePathRef.current = fullPath;
        mediaRecorderRef.current = file;
        
        // Set up success/error callbacks
        file.onSuccess.subscribe(() => console.log('Media ready'));
        file.onError.subscribe(err => console.error('Media error:', err));
        
        console.log('Starting recording...');
        file.startRecord();

        setIsRecording(true);
        setRecordingTime(0);
        timerRef.current = setInterval(() => setRecordingTime((prev) => prev + 1), 1000);
        console.log('Recording started successfully');
      } catch (err) {
        console.error('Native recording error name:', err instanceof Error ? err.name : typeof err);
        console.error('Native recording error message:', err instanceof Error ? err.message : String(err));
        console.error('Native recording error stack:', err instanceof Error ? err.stack : 'n/a');
        setError('Failed to start recording (native): ' + (err instanceof Error ? err.message : String(err)));
      }
    }
  };

  const stopRecording = async () => {
    vibrate(HapticPatterns.SUCCESS);
    if (timerRef.current) clearInterval(timerRef.current);
    setIsRecording(false);

    if (Capacitor.getPlatform() === 'web') {
      mediaRecorderRef.current?.stop();
    } else {
      try {
        const file = mediaRecorderRef.current;
        if (!file) {
          throw new Error('No active recording');
        }

        console.log('Stopping recording...');
        file.stopRecord();

        console.log('Waiting for recording to finalize...');
        // Wait a bit longer on iOS
        await new Promise(resolve => setTimeout(resolve, Capacitor.getPlatform() === 'ios' ? 2500 : 2000));

        try {
          // Read the file using File plugin
          console.log('Attempting to read file:', filePathRef.current);
          const File = (await import('@awesome-cordova-plugins/file')).File;
          
          const fileEntry = await File.resolveLocalFilesystemUrl(filePathRef.current!);
          const file = await new Promise((resolve, reject) => {
            fileEntry.file(resolve, reject);
          });
          
          const arrayBuffer = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsArrayBuffer(file);
          });
          
          // Convert ArrayBuffer to Base64
          const bytes = new Uint8Array(arrayBuffer);
          let binary = '';
          for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
          }
          const data = window.btoa(binary);

          console.log('File read successfully, data length:', data?.length || 0);

          // Convert base64 to blob
          console.log('Converting base64 to blob...');
          const byteString = atob(data);
          const byteArray = new Uint8Array(byteString.length);
          for (let i = 0; i < byteString.length; i++) {
            byteArray[i] = byteString.charCodeAt(i);
          }
          const blob = new Blob([byteArray], { type: 'audio/mpeg' });
          console.log('Blob created, size:', blob.size);

          const extension = Capacitor.getPlatform() === 'ios' ? '.m4a' : '.mp3';
          const filename = `${isIdeasPage ? 'ideabank' : 'recordings'}/${user!.uid}/${Date.now()}${extension}`;
          console.log('Preparing to upload to Firebase:', filename);
          const storageRef = ref(storage, filename);

          setIsUploading(true);
          console.log('Starting upload...');
          await uploadBytes(storageRef, blob);
          console.log('Upload complete, getting download URL...');
          const downloadUrl = await getDownloadURL(storageRef);
          console.log('Got download URL:', downloadUrl);
          onAudioSave(downloadUrl);
          setIsUploading(false);

          // Clean up the temporary file
          console.log('Cleaning up temporary file...');
          await Filesystem.deleteFile({
            path: filePathRef.current!,
            directory: Directory.Cache
          }).catch(e => console.error('Error deleting temp file:', e));
          console.log('Cleanup complete');
        } catch (err) {
          console.error('Detailed file handling error:', err);
          if (err instanceof Error) {
            console.error('Error name:', err.name);
            console.error('Error message:', err.message);
            console.error('Error stack:', err.stack);
          }
          throw err;
        }
      } catch (err) {
        console.error('Native stop/upload error:', err);
        setError('Failed to save audio. Error: ' + (err instanceof Error ? err.message : String(err)));
      } finally {
        // Always try to release the media resource
        try {
          mediaRecorderRef.current?.release();
        } catch (releaseErr) {
          console.error('Error releasing media:', releaseErr);
        }
      }
    }
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (mediaRecorderRef.current) {
        try {
          mediaRecorderRef.current.release();
        } catch (err) {
          console.error('Error releasing media:', err);
        }
      }
    };
  }, []);

  return (
    <div className="flex flex-col space-y-2 w-full">
      {error && <div className="text-red-500 text-sm">{error}</div>}
      <div className="flex flex-col md:flex-row items-center gap-4 w-full">
        <button
          onClick={isRecording ? stopRecording : startRecording}
          className={`px-4 py-3 rounded-lg font-medium transition-colors w-full md:w-auto ${
            isRecording ? 'bg-red-500 text-white hover:bg-red-600' : 'bg-red-500 text-white hover:bg-red-600'
          }`}
        >
          {isRecording ? 'Stop Recording' : 'Start Recording'}
        </button>
        {isRecording && (
          <span className="text-sm text-gray-600">
            {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
          </span>
        )}
      </div>
    </div>
  );
}
